import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, Upload, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InstitutionalApplyPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    institution_name: '',
    registration_number: '',
    country: '',
    address: '',
    contact_person: '',
    email: '',
    phone: '',
    accreditation_type: '',
    courses_conducted: '',
    why_imcac_accreditation: '',
    monthly_student_intake: '',
    faculty_founder_bio: '',
    declaration: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.declaration) { toast.error('You must accept the declaration'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/applications/institutional`, form, { headers: { Authorization: `Bearer ${token}` } });
      setSubmitted(true);
      toast.success('Application submitted successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#F8FAFC] py-16" data-testid="institutional-apply-success">
        <Card className="imcac-card max-w-lg w-full mx-4">
          <CardContent className="p-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#1B2A4A] font-heading mb-2">Application Submitted</h2>
            <p className="text-sm text-slate-600 mb-6">Your institutional accreditation application has been received and will be reviewed by the Accreditation Committee.</p>
            <Button onClick={() => window.location.href = '/dashboard'} className="bg-[#1B2A4A] text-white rounded-sm">Go to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="institutional-apply-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">Division of Institutional Accreditation</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">Application for Accreditation</h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
          <p className="text-lg text-slate-300 mt-4 max-w-2xl">
            For training academies and institutions seeking IMCAC accreditation for their aesthetic medicine programs.
          </p>
        </div>
      </section>

      <section className="imcac-section bg-[#F8FAFC]">
        <div className="imcac-container max-w-3xl">
          <Card className="imcac-card">
            <CardContent className="p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-200">
                <div className="w-10 h-10 bg-[#1B2A4A] rounded-sm flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-[#C5A572]" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[#1B2A4A] font-heading">Institutional Accreditation Application</h2>
                  <p className="text-xs text-slate-500">Complete all fields to apply for programme accreditation</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Institution Details */}
                <div>
                  <h3 className="text-sm font-semibold text-[#1B2A4A] mb-3 uppercase tracking-wider">Institution Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Institution Name *</Label>
                      <Input value={form.institution_name} onChange={e => setForm({...form, institution_name: e.target.value})} required className="rounded-sm mt-1" placeholder="ABC Academy of Aesthetic Medicine" data-testid="inst-name" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Registration Number *</Label>
                      <Input value={form.registration_number} onChange={e => setForm({...form, registration_number: e.target.value})} required className="rounded-sm mt-1" placeholder="Institution registration number" data-testid="inst-reg" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Country *</Label>
                      <Input value={form.country} onChange={e => setForm({...form, country: e.target.value})} required className="rounded-sm mt-1" placeholder="India" data-testid="inst-country" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Monthly Student Intake *</Label>
                      <Input type="number" value={form.monthly_student_intake} onChange={e => setForm({...form, monthly_student_intake: e.target.value})} required className="rounded-sm mt-1" placeholder="e.g., 25" data-testid="inst-intake" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <Label className="text-sm font-medium text-slate-700">Full Address *</Label>
                    <Textarea value={form.address} onChange={e => setForm({...form, address: e.target.value})} required rows={2} className="rounded-sm mt-1 resize-none" placeholder="Complete address including city, state, and postal code" data-testid="inst-address" />
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h3 className="text-sm font-semibold text-[#1B2A4A] mb-3 uppercase tracking-wider">Contact Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Contact Person *</Label>
                      <Input value={form.contact_person} onChange={e => setForm({...form, contact_person: e.target.value})} required className="rounded-sm mt-1" placeholder="Dr. John Smith" data-testid="inst-contact" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Email *</Label>
                      <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required className="rounded-sm mt-1" placeholder="contact@academy.com" data-testid="inst-email" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Phone *</Label>
                      <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required className="rounded-sm mt-1" placeholder="+91 98765 43210" data-testid="inst-phone" />
                    </div>
                  </div>
                </div>

                {/* Programme Details */}
                <div>
                  <h3 className="text-sm font-semibold text-[#1B2A4A] mb-3 uppercase tracking-wider">Programme Details</h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Name of Courses Conducted in Academy *</Label>
                      <Textarea 
                        value={form.courses_conducted} 
                        onChange={e => setForm({...form, courses_conducted: e.target.value})} 
                        required 
                        rows={3} 
                        className="rounded-sm mt-1 resize-none" 
                        placeholder="List all courses offered (e.g., Certificate in Aesthetic Medicine, Diploma in Cosmetology, Advanced Injectables Training, etc.)"
                        data-testid="inst-courses" 
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Accreditation Type *</Label>
                      <Select value={form.accreditation_type} onValueChange={v => setForm({...form, accreditation_type: v})}>
                        <SelectTrigger className="rounded-sm mt-1" data-testid="inst-type"><SelectValue placeholder="Select accreditation type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Initial Accreditation">Initial Accreditation</SelectItem>
                          <SelectItem value="Re-Accreditation">Re-Accreditation</SelectItem>
                          <SelectItem value="Program-Specific Accreditation">Program-Specific Accreditation</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Why IMCAC Accreditation? *</Label>
                      <Textarea 
                        value={form.why_imcac_accreditation} 
                        onChange={e => setForm({...form, why_imcac_accreditation: e.target.value})} 
                        required 
                        rows={4} 
                        className="rounded-sm mt-1 resize-none" 
                        placeholder="Explain why your institution is seeking IMCAC accreditation, how it aligns with your educational mission, and what benefits you expect from accreditation..."
                        data-testid="inst-why" 
                      />
                    </div>
                  </div>
                </div>

                {/* Faculty Information */}
                <div>
                  <h3 className="text-sm font-semibold text-[#1B2A4A] mb-3 uppercase tracking-wider">Faculty / Founder Information</h3>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Bio of Main Faculty or Founder *</Label>
                    <Textarea 
                      value={form.faculty_founder_bio} 
                      onChange={e => setForm({...form, faculty_founder_bio: e.target.value})} 
                      required 
                      rows={5} 
                      className="rounded-sm mt-1 resize-none" 
                      placeholder="Provide a detailed biography of the main faculty member or founder, including qualifications, experience, certifications, publications, and any notable achievements in aesthetic medicine education..."
                      data-testid="inst-bio" 
                    />
                  </div>
                </div>

                {/* Document Upload Notice */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-sm">
                  <p className="text-sm font-medium text-slate-700 mb-2">Supporting Documents</p>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Upload className="h-3.5 w-3.5" /> 
                    <span>Document upload (curriculum, faculty credentials, infrastructure details) will be requested upon preliminary approval</span>
                  </div>
                </div>

                {/* Declaration */}
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-sm">
                  <Checkbox checked={form.declaration} onCheckedChange={v => setForm({...form, declaration: v})} className="mt-1" data-testid="inst-declaration" />
                  <label className="text-sm text-slate-700 leading-relaxed cursor-pointer">
                    I declare that all information provided is accurate and complete. I understand that IMCAC accreditation validates programme quality and competency standards, and does not confer degree-granting status. I agree to comply with IMCAC accreditation standards and periodic reviews.
                  </label>
                </div>

                <Button type="submit" disabled={loading || !form.declaration} className="w-full bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm py-3" data-testid="inst-submit">
                  {loading ? 'Submitting Application...' : 'Submit Accreditation Application'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
